import { AuthenticationService } from './../authentication/authentication.service';
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ClientConstants, LocalStorageService, LocalStorageKeys, LoggedInUserService, WorkerService } from '@formbird/services';
import { SharedUrlRoutes } from '@formbird/shared';
import { Location } from '@angular/common';
import { Observable, defer, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

const serverRoutes = SharedUrlRoutes.serverRoutes;

const AUTH_ROUTES = [
    '/login', '/auth/login',
    '/forgotpass', '/auth/forgotpass',
    '/signup', '/auth/signup',
    '/resetpass', '/auth/resetpass',
    '/activateAccount', '/auth/activateAccount'
];

/** Marks a retried request so a second 401 on it doesn't trigger another public re-auth attempt. */
export const FB_AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
    private location = inject(Location);
    private authenticationService = inject(AuthenticationService);
    private localStorageService = inject(LocalStorageService);
    private workerService = inject(WorkerService);
    private loggedInUserService = inject(LoggedInUserService);

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            tap(event => {
                // user is authenticated
            }),
            catchError((error: HttpErrorResponse) => this.handleError(error, req, next))
        );
    }

    private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (error.status !== ClientConstants.HTTP_STATUS_LOGIN_REJECTED) {
            return throwError(() => error);
        }

        this.workerService.terminateAllWorkers();

        const alreadyRetried = req.context.get(FB_AUTH_RETRIED);
        const isPublicReauthRequest = req.url.includes(serverRoutes.publicLogin);
        const publicAccountURL = this.loggedInUserService.getPublicAccountURL();

        if (!alreadyRetried && !isPublicReauthRequest && publicAccountURL) {
            return this.getPublicReauth$(publicAccountURL).pipe(
                switchMap(() => next.handle(req.clone({ context: req.context.set(FB_AUTH_RETRIED, true) }))),
                // Only treat the public reauth itself as having failed if the retried request is
                // STILL rejected with 401 - any other failure on the retry (e.g. a downstream 500
                // unrelated to the session) is not evidence that reauth failed and must not force
                // the user to the login screen.
                catchError((retryError) => retryError?.status === ClientConstants.HTTP_STATUS_LOGIN_REJECTED
                    ? this.fallback(retryError)
                    : throwError(() => retryError))
            );
        }

        return this.fallback(error);
    }

    /** Preserves the original redirect-to-login behavior for non-public accounts and unrecoverable public re-auth failures. */
    private fallback(error: HttpErrorResponse): Observable<HttpEvent<any>> {
        const checkPublicUser = this.localStorageService.getItem(LocalStorageKeys.CHECK_PUBLIC_USER);
        if (!AUTH_ROUTES.find(route => this.location.path().startsWith(route)) && !checkPublicUser) {
            this.authenticationService.showLogin();
        }
        return throwError(() => error);
    }

    /**
     * LoggedInUserService.authenticatePublicUser() itself coalesces concurrent callers onto a
     * single in-flight request now, so this no longer needs its own sharing/replay wrapper — just
     * turning the promise into an observable for the switchMap chain below.
     */
    private getPublicReauth$(accountPublicURL: string): Observable<any> {
        return defer(() => from(this.loggedInUserService.authenticatePublicUser(accountPublicURL)));
    }
}
